import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, message, Upload } from 'antd';
import {
	CloseOutlined,
	FileImageOutlined,
	FilePdfOutlined,
	FileTextOutlined,
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import ProformaInvoiceDetailsFields, {
	PROFORMA_API_DATE_FORMAT,
} from './ProformaInvoiceDetailsFields';
import { uploadProformaInvoice } from 'store/financeSlice';
import { uploadProformaInvoiceV2 } from 'store/financeSliceV2';
import { getUIVersion } from 'utils/router-static-data';

const StyledModal = styled(Modal)`
	.ant-modal-content {
		border-radius: 4px;
	}
	.ant-modal-body {
		padding: 36px 24px;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 31px;
	}
	.ant-modal-header {
		display: none;
	}
	.ant-modal-close {
		display: none;
	}
`;

const ModalTitle = styled.h2`
	margin: 0;
	font-size: 22px;
	font-weight: 600;
	color: #000;
	font-family: 'Inter', sans-serif;
`;

const UploadArea = styled.div`
	width: 348px;
	border: 1px dashed #c6c6c8;
	border-radius: 4px;
	padding: 16px 20px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
`;

const UploadButtonsRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 12px;
	width: 100%;
`;

const UploadHint = styled.p`
	margin: 0;
	font-size: 12px;
	font-family: 'Inter', sans-serif;
	font-weight: 400;
	color: #99999b;
	text-align: center;
	letter-spacing: -0.36px;
	width: 100%;
`;

const UploadButton = styled(Button)`
	height: 40px;
	padding: 8px 16px;
	border: 1px solid #c9c9c9;
	border-radius: 4px;
	background: #ffffff;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 10px;

	span {
		font-size: 16px;
		font-weight: 500;
		font-family: 'Inter', sans-serif;
		color: #18405d;
	}

	&:hover {
		border-color: #18405d;
		background: #ffffff;
	}
`;

const UploadIcon = styled.span`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;

	svg {
		width: 20px;
		height: 20px;
		color: #18405d;
	}
`;

const FilePreview = styled.div`
	width: 346px;
	height: 64px;
	padding: 8px 0;
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const FileInfo = styled.div`
	display: flex;
	align-items: center;
	gap: 14px;
	flex: 1;
	min-width: 0;
`;

const FileIcon = styled.span`
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;

	svg {
		width: 24px;
		height: 24px;
	}
`;

const FileName = styled.span`
	font-size: 14px;
	font-weight: 500;
	font-family: 'Inter', sans-serif;
	color: #5dc1ff;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const RemoveButton = styled.button`
	flex-shrink: 0;
	margin-left: 12px;
	width: 24px;
	height: 24px;
	border: none;
	background: transparent;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0;

	svg {
		width: 24px;
		height: 24px;
		color: #000;
	}

	&:hover svg {
		color: #ff4d4f;
	}
`;

const ButtonContainer = styled.div`
	display: flex;
	gap: 16px;
`;

const CancelButton = styled(Button)`
	width: 136px;
	height: 40px;
	padding: 8px 16px;
	border: 1px solid #143e60;
	border-radius: 4px;
	background: #ffffff;

	span {
		font-size: 16px;
		font-weight: 500;
		font-family: 'Inter', sans-serif;
		color: #143e60;
	}

	&:hover {
		border-color: #1a4d7a;
		background: #ffffff;
	}
`;

const SubmitButton = styled(Button)`
	width: 136px;
	height: 40px;
	padding: 8px 16px;
	border: none;
	border-radius: 4px;
	background: #18405d;

	span {
		font-size: 16px;
		font-weight: 500;
		font-family: 'Inter', sans-serif;
		color: #ffffff;
	}

	&:hover {
		background: #1a4d7a;
	}

	&:disabled {
		background: #d9d9d9;
		color: #8c8c8c;
	}
`;

const LoadingContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 31px;
	padding: 40px 0;
`;

const LoadingText = styled.p`
	margin: 0;
	font-size: 16px;
	font-weight: 500;
	font-family: 'Inter', sans-serif;
	color: #000;
`;

const GenerateButton = styled(Button)`
	height: 40px;
	padding: 8px 16px;
	border: 1px solid #18405d;
	border-radius: 4px;
	background: #18405d;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;

	span {
		font-size: 16px;
		font-weight: 500;
		font-family: 'Inter', sans-serif;
		color: #ffffff;
	}

	&:hover {
		background: #1a4d7a;
		border-color: #1a4d7a;
	}

	&:disabled {
		background: #d9d9d9;
		border-color: #d9d9d9;
		color: #8c8c8c;
	}
`;

const SpinnerWrapper = styled.div`
	width: 91px;
	height: 91px;
	position: relative;
`;

const Spinner = styled.svg`
	width: 100%;
	height: 100%;
	animation: rotate 1s linear infinite;

	@keyframes rotate {
		from {
			transform: rotate(0deg);
		}
		to {
			transform: rotate(360deg);
		}
	}
`;

// Upload icon SVG path
const uploadIconPath = (
	<>
		<path
			d="M12 16V4M12 4L8 8M12 4L16 8"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path
			d="M3 17V18C3 19.6569 4.34315 21 6 21H18C19.6569 21 21 19.6569 21 18V17"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</>
);

// Helper function to get file icon based on extension
const getFileIcon = (filename) => {
	const extension = filename?.split('.').pop()?.toLowerCase();
	if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
		return <FileImageOutlined style={{ color: '#5dc1ff' }} />;
	}
	return <FilePdfOutlined style={{ color: '#5dc1ff' }} />;
};

function UploadProformaModal({ visible, onClose, record, onUploadSuccess, onGenerate }) {
	const dispatch = useDispatch();
	const isV2 = useMemo(() => getUIVersion() === 'new', []);
	const [file, setFile] = useState(null);
	const [invoiceNumber, setInvoiceNumber] = useState('');
	const [invoiceDate, setInvoiceDate] = useState(null);
	const [uploading, setUploading] = useState(false);
	const [generating, setGenerating] = useState(false);

	// Reset state when modal closes
	const handleClose = useCallback(() => {
		setFile(null);
		setInvoiceNumber('');
		setInvoiceDate(null);
		setUploading(false);
		setGenerating(false);
		onClose();
	}, [onClose]);

	// Handle generate pro-forma invoice
	const handleGenerateClick = useCallback(() => {
		if (!onGenerate || !record) return;

		setGenerating(true);
		onGenerate(record)
			.then((result) => {
				if (result) {
					const formattedResult = {
						vendor_proforma_invoice_number:
							result.proforma_invoice_number || result.invoice_number,
						vendor_proforma_invoice_url: result.pdf_url || result.document_url,
						...result,
					};
					message.success('Pro-forma invoice generated successfully');
					if (onUploadSuccess) {
						onUploadSuccess(formattedResult);
					}
					handleClose();
				}
			})
			.catch((error) => {
				message.error(error?.message || 'Failed to generate pro-forma invoice');
			})
			.finally(() => {
				setGenerating(false);
			});
	}, [onGenerate, record, onUploadSuccess, handleClose]);

	// Handle file selection
	const handleBeforeUpload = useCallback((selectedFile) => {
		// Validate file type
		const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
		if (!allowedTypes.includes(selectedFile.type)) {
			message.error('Please upload JPEG, PNG or PDF file only');
			return false;
		}

		// Validate file size (50MB max)
		const maxSize = 50 * 1024 * 1024; // 50MB in bytes
		if (selectedFile.size > maxSize) {
			message.error('File size should not exceed 50MB');
			return false;
		}

		setFile(selectedFile);
		return false; // Prevent auto upload
	}, []);

	// Handle file removal
	const handleRemoveFile = useCallback(() => {
		setFile(null);
	}, []);

	// Handle submit
	const handleSubmit = useCallback(async () => {
		if (!file) {
			message.error('Please select a file to upload');
			return;
		}

		if (!record?.id) {
			message.error('Invalid record');
			return;
		}

		setUploading(true);

		try {
			const uploadAction = isV2 ? uploadProformaInvoiceV2 : uploadProformaInvoice;
			const result = await dispatch(
				uploadAction({
					file,
					id: record.id,
					invoiceNumber,
					invoiceDate: invoiceDate ? invoiceDate.format(PROFORMA_API_DATE_FORMAT) : null,
				})
			);

			if (result) {
				message.success('Pro-forma invoice uploaded successfully');
				if (onUploadSuccess) {
					onUploadSuccess(result);
				}
				handleClose();
			}
		} catch (error) {
			message.error(error?.message || 'Failed to upload pro-forma invoice');
		} finally {
			setUploading(false);
		}
	}, [file, record, dispatch, onUploadSuccess, handleClose, isV2, invoiceNumber, invoiceDate]);

	const isLoading = uploading || generating;

	return (
		<StyledModal
			open={visible}
			onCancel={isLoading ? undefined : handleClose}
			footer={null}
			width={420}
			centered
			destroyOnClose
			closable={!isLoading}
			maskClosable={!isLoading}
		>
			<ModalTitle>Upload Pro-Forma</ModalTitle>

			{isLoading ? (
				<LoadingContainer>
					<SpinnerWrapper>
						<Spinner viewBox="0 0 91 91" fill="none" xmlns="http://www.w3.org/2000/svg">
							<circle
								cx="45.5"
								cy="45.5"
								r="40"
								stroke="#E8E8E8"
								strokeWidth="8"
								fill="none"
							/>
							<path
								d="M45.5 5.5C67.591 5.5 85.5 23.409 85.5 45.5"
								stroke="#5DC1FF"
								strokeWidth="8"
								strokeLinecap="round"
								fill="none"
							/>
						</Spinner>
					</SpinnerWrapper>
					<LoadingText>
						{generating
							? 'Generating Pro-Forma Invoice'
							: 'Uploading Pro-Forma Invoice'}
					</LoadingText>
				</LoadingContainer>
			) : (
				<>
					<UploadArea>
						<UploadHint>JPEG, PNG and PDF, formats up to 50 MB</UploadHint>
						<UploadButtonsRow>
							<GenerateButton onClick={handleGenerateClick}>
								<FileTextOutlined style={{ fontSize: 16, color: '#ffffff' }} />
								<span>Generate</span>
							</GenerateButton>
							<Upload
								beforeUpload={handleBeforeUpload}
								showUploadList={false}
								accept=".jpg,.jpeg,.png,.pdf"
							>
								<UploadButton>
									<UploadIcon>
										<svg
											viewBox="0 0 24 24"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											{uploadIconPath}
										</svg>
									</UploadIcon>
									<span>Upload File</span>
								</UploadButton>
							</Upload>
						</UploadButtonsRow>
					</UploadArea>

					{file && (
						<FilePreview>
							<FileInfo>
								<FileIcon>{getFileIcon(file.name)}</FileIcon>
								<FileName title={file.name}>{file.name}</FileName>
							</FileInfo>
							<RemoveButton onClick={handleRemoveFile} type="button">
								<CloseOutlined />
							</RemoveButton>
						</FilePreview>
					)}

					{file && isV2 && (
						<ProformaInvoiceDetailsFields
							idPrefix="upload-proforma"
							invoiceNumber={invoiceNumber}
							invoiceDate={invoiceDate}
							onInvoiceNumberChange={setInvoiceNumber}
							onInvoiceDateChange={setInvoiceDate}
						/>
					)}

					<ButtonContainer>
						<CancelButton onClick={handleClose}>
							<span>Cancel</span>
						</CancelButton>
						<SubmitButton onClick={handleSubmit} disabled={!file}>
							<span>Upload</span>
						</SubmitButton>
					</ButtonContainer>
				</>
			)}
		</StyledModal>
	);
}

UploadProformaModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	record: PropTypes.shape({
		id: PropTypes.number,
	}),
	onUploadSuccess: PropTypes.func,
	onGenerate: PropTypes.func.isRequired,
};

UploadProformaModal.defaultProps = {
	record: null,
	onUploadSuccess: null,
};

export default UploadProformaModal;
